import React, { useState } from 'react';
import axios from 'axios';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Calculator, Scale, Ruler, AlertTriangle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BMICalculatorStep() {
    const { bmi_data, updateBMIData, calculatedBMI, setCalculatedBMI } = useReferral();
    const [measurementSystem, setMeasurementSystem] = useState('metric');
    const [isCalculating, setIsCalculating] = useState(false);

    const handleMetricChange = (field, value) => {
        const numValue = value === '' ? null : parseFloat(value);
        updateBMIData({ [field]: numValue });
    };

    const handleImperialChange = (field, value) => {
        const numValue = value === '' ? null : parseFloat(value);
        updateBMIData({ [field]: numValue });
    };

    const calculateBMI = async () => {
        setIsCalculating(true);
        try {
            const response = await axios.post(`${API}/calculate-bmi`, bmi_data);
            setCalculatedBMI(response.data);
        } catch (error) {
            console.error('BMI calculation error:', error);
        } finally {
            setIsCalculating(false);
        }
    };

    const getBMIColor = (category) => {
        switch (category) {
            case 'normal': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
            case 'underweight': return 'text-amber-600 bg-amber-50 border-amber-200';
            case 'overweight': return 'text-amber-600 bg-amber-50 border-amber-200';
            case 'obese_class_1': return 'text-orange-600 bg-orange-50 border-orange-200';
            case 'obese_class_2': return 'text-rose-600 bg-rose-50 border-rose-200';
            case 'obese_class_3': return 'text-rose-700 bg-rose-100 border-rose-300';
            default: return 'text-slate-600 bg-slate-50 border-slate-200';
        }
    };

    const getBMIIcon = (category) => {
        if (category === 'normal') {
            return <CheckCircle className="w-6 h-6 text-emerald-600" />;
        }
        if (category === 'obese_class_2' || category === 'obese_class_3') {
            return <AlertTriangle className="w-6 h-6 text-rose-600" />;
        }
        return <Scale className="w-6 h-6 text-amber-600" />;
    };

    return (
        <Card className="card-clinical">
            <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Calculator className="w-5 h-5 text-teal-600" />
                    BMI Calculation
                </CardTitle>
                <CardDescription>
                    Calculate the patient&apos;s Body Mass Index. BMI ≥ 40 may affect surgical eligibility per NICE guidelines.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <Tabs value={measurementSystem} onValueChange={setMeasurementSystem} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                        <TabsTrigger value="metric" data-testid="metric-tab">Metric (kg/cm)</TabsTrigger>
                        <TabsTrigger value="imperial" data-testid="imperial-tab">Imperial (st/ft)</TabsTrigger>
                    </TabsList>

                    <TabsContent value="metric" className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="height_cm" className="label-clinical flex items-center gap-2">
                                    <Ruler className="w-4 h-4 text-slate-400" />
                                    Height (cm)
                                </Label>
                                <Input
                                    id="height_cm"
                                    type="number"
                                    placeholder="175"
                                    value={bmi_data.height_cm || ''}
                                    onChange={(e) => handleMetricChange('height_cm', e.target.value)}
                                    className="input-clinical"
                                    data-testid="height-cm-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="weight_kg" className="label-clinical flex items-center gap-2">
                                    <Scale className="w-4 h-4 text-slate-400" />
                                    Weight (kg)
                                </Label>
                                <Input
                                    id="weight_kg"
                                    type="number"
                                    placeholder="75"
                                    value={bmi_data.weight_kg || ''}
                                    onChange={(e) => handleMetricChange('weight_kg', e.target.value)}
                                    className="input-clinical"
                                    data-testid="weight-kg-input"
                                />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="imperial" className="space-y-6">
                        <div className="space-y-4">
                            <Label className="label-clinical flex items-center gap-2">
                                <Ruler className="w-4 h-4 text-slate-400" />
                                Height
                            </Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="height_feet" className="text-xs text-slate-500">Feet</Label>
                                    <Input
                                        id="height_feet"
                                        type="number"
                                        placeholder="5"
                                        value={bmi_data.height_feet || ''}
                                        onChange={(e) => handleImperialChange('height_feet', e.target.value)}
                                        className="input-clinical"
                                        data-testid="height-feet-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="height_inches" className="text-xs text-slate-500">Inches</Label>
                                    <Input
                                        id="height_inches"
                                        type="number"
                                        placeholder="9"
                                        value={bmi_data.height_inches || ''}
                                        onChange={(e) => handleImperialChange('height_inches', e.target.value)}
                                        className="input-clinical"
                                        data-testid="height-inches-input"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <Label className="label-clinical flex items-center gap-2">
                                <Scale className="w-4 h-4 text-slate-400" />
                                Weight
                            </Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="weight_stones" className="text-xs text-slate-500">Stones</Label>
                                    <Input
                                        id="weight_stones"
                                        type="number"
                                        placeholder="12"
                                        value={bmi_data.weight_stones || ''}
                                        onChange={(e) => handleImperialChange('weight_stones', e.target.value)}
                                        className="input-clinical"
                                        data-testid="weight-stones-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="weight_pounds" className="text-xs text-slate-500">Pounds</Label>
                                    <Input
                                        id="weight_pounds"
                                        type="number"
                                        placeholder="7"
                                        value={bmi_data.weight_pounds || ''}
                                        onChange={(e) => handleImperialChange('weight_pounds', e.target.value)}
                                        className="input-clinical"
                                        data-testid="weight-pounds-input"
                                    />
                                </div>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>

                <Button 
                    onClick={calculateBMI} 
                    disabled={isCalculating}
                    className="w-full btn-primary"
                    data-testid="calculate-bmi-btn"
                >
                    <Calculator className="w-4 h-4 mr-2" />
                    {isCalculating ? 'Calculating...' : 'Calculate BMI'}
                </Button>

                {/* BMI Result */}
                {calculatedBMI && (
                    <div className={`rounded-xl p-6 border-2 ${getBMIColor(calculatedBMI.category)}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium opacity-80">Calculated BMI</p>
                                <p className="text-4xl font-bold mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {calculatedBMI.bmi}
                                </p>
                                <p className="text-sm mt-2 font-medium">
                                    {calculatedBMI.interpretation}
                                </p>
                            </div>
                            <div>
                                {getBMIIcon(calculatedBMI.category)}
                            </div>
                        </div>

                        {(calculatedBMI.category === 'obese_class_2' || calculatedBMI.category === 'obese_class_3') && (
                            <div className="mt-4 pt-4 border-t border-current/20">
                                <p className="text-sm flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <span>
                                        {calculatedBMI.category === 'obese_class_3' 
                                            ? 'BMI ≥ 40: Per NICE NG226 and BOA guidelines, patients with BMI ≥ 40 may not be eligible for elective joint replacement surgery without weight management intervention.'
                                            : 'BMI 35-40: Consider referral to weight management service alongside orthopedic pathway due to increased surgical risk.'
                                        }
                                    </span>
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* BMI Reference Chart */}
                <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">BMI Categories (WHO)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-amber-400"></span>
                            <span>Underweight: &lt;18.5</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                            <span>Normal: 18.5-24.9</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                            <span>Overweight: 25-29.9</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                            <span>Obese I: 30-34.9</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-rose-500"></span>
                            <span>Obese II: 35-39.9</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-rose-700"></span>
                            <span>Obese III: ≥40</span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
